import SparkMD5 from 'spark-md5'
import { CryptoService } from './CryptoService'

/**
 * Converte un ArrayBuffer in una stringa base64.
 * @param buffer L'ArrayBuffer da convertire.
 * @returns La rappresentazione in formato base64 dell'ArrayBuffer.
 */
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  if (typeof btoa === 'function') {
    return btoa(String.fromCharCode.apply(null, new Uint8Array(buffer) as any))
  } else {
    return Buffer.from(buffer).toString('base64')
  }
}

/**
 * Converte una stringa utf-8 in una stringa base64.
 * @param text La stringa utf-8 da convertire.
 * @returns La rappresentazione in formato base64 della stringa utf-8.
 */
function UTF8StringTobase64(text: string): string {
  const encoder = new TextEncoder()
  const arrayBuffer = encoder.encode(text)
  return arrayBufferToBase64(arrayBuffer)
}

/**
 * Converte una stringa utf-8 in una stringa base64.
 * @param text La stringa utf-8 da convertire.
 * @returns La rappresentazione in ArrayBuffer della stringa utf-8.
 */
function UTF8StringToArrayBuffer(text: string): ArrayBuffer {
  const encoder = new TextEncoder()
  const arrayBuffer = encoder.encode(text)
  return arrayBuffer
}

/**
 * Converte una stringa base64 in un ArrayBuffer.
 * @param base64 La stringa base64 da convertire.
 * @returns L'ArrayBuffer corrispondente.
 */
function base64ToArrayBuffer(base64: string): ArrayBuffer {
  if (typeof atob === 'function') {
    const binaryString = atob(base64)
    const len = binaryString.length
    const bytes = new Uint8Array(len)
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i)
    }
    return bytes.buffer
  } else {
    return Buffer.from(base64, 'base64')
  }
}

/**
 * Converte una stringa base64 in una stringa utf-8.
 * @param base64 La stringa base64 da convertire.
 * @returns La stringa utf-8 corrispondente.
 */
function base64ToUTF8String(base64: string): string {
  const arrayBuffer = base64ToArrayBuffer(base64)
  const decoder = new TextDecoder('utf-8')
  return decoder.decode(arrayBuffer)
}

/**
 * Converte un'ArrayBuffer in una stringa utf-8.
 * @param arrayBuffer L'ArrayBuffer da convertire.
 * @returns La stringa utf-8 corrispondente.
 */
function arrayBufferToUTF8String(arrayBuffer: ArrayBuffer): string {
  const decoder = new TextDecoder('utf-8')
  return decoder.decode(arrayBuffer)
}

/**
 * Concatena due ArrayBuffer.
 * @param buffer1 L'ArrayBuffer da concatenare.
 * @param buffer2 L'ArrayBuffer da concatenare.
 * @returns L'ArrayBuffer risultante.
 */
function concatArrayBuffer(buffer1: ArrayBuffer, buffer2: ArrayBuffer) {
  var tmp = new Uint8Array(buffer1.byteLength + buffer2.byteLength)
  tmp.set(new Uint8Array(buffer1), 0)
  tmp.set(new Uint8Array(buffer2), buffer1.byteLength)
  return tmp.buffer
}

/**
 * Confronta due ArrayBuffer.
 * @param buffer1 L'ArrayBuffer da confrontare.
 * @param buffer2 L'ArrayBuffer da confrontare.
 * @returns true se gli array sono uguali.
 */
function arrayBufferEquals(buffer1: ArrayBuffer, buffer2: ArrayBuffer) {
  const buffer1UInt8Array = new Uint8Array(buffer1)
  const buffer2UInt8Array = new Uint8Array(buffer2)
  return (
    buffer1UInt8Array.length === buffer2UInt8Array.length &&
    buffer1UInt8Array.every(
      (value, index) => value === buffer2UInt8Array[index]
    )
  )
}

async function computeFileHash(file: File): Promise<string> {
  const buffer = await file.arrayBuffer()
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  const hash = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
  return hash
}

const fromHexToBase64 = (hex: string): string => {
  const buffer = Buffer.from(hex, 'hex')
  return buffer.toString('base64')
}

const MD5_CHUNK_SIZE = 2097152 // 2MB

const computeMD5 = async (data: Buffer | File): Promise<string> => {
  const spark = new SparkMD5.ArrayBuffer()

  if (data instanceof File) {
    // Il file viene letto un blocco alla volta: caricarlo tutto con
    // arrayBuffer() ne terrebbe in memoria una copia intera e bloccherebbe
    // il thread principale per l'intera durata del calcolo (~10s ogni 100MB).
    // L'await fra un blocco e l'altro restituisce il controllo al browser.
    for (let start = 0; start < data.size; start += MD5_CHUNK_SIZE) {
      const end = Math.min(start + MD5_CHUNK_SIZE, data.size)
      spark.append(await data.slice(start, end).arrayBuffer())
    }
    return spark.end()
  }

  const chunks = Math.ceil(data.length / MD5_CHUNK_SIZE)
  for (let i = 0; i < chunks; i++) {
    const start = i * MD5_CHUNK_SIZE
    const end = Math.min(start + MD5_CHUNK_SIZE, data.length)
    spark.append(data.slice(start, end))
  }

  return spark.end() // Ritorna l'MD5 in formato hex
}

const generateDek = async (): Promise<string> => {
  const dek = await CryptoService.getInstance().generateDek()
  return arrayBufferToBase64(dek)
}

export const CryptoUtilities = {
  arrayBufferToBase64,
  UTF8StringTobase64,
  base64ToArrayBuffer,
  base64ToUTF8String,
  UTF8StringToArrayBuffer,
  arrayBufferToUTF8String,
  concatArrayBuffer,
  arrayBufferEquals,
  computeFileHash,

  fromHexToBase64,
  computeMD5,
  generateDek,
}

export default CryptoUtilities
